import React, { useEffect, useState, useRef } from "react";
import Skeleton from '@mui/material/Skeleton';
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import videojs from "video.js";
import chaptersMore from "videojs-chapters-extension";
import "video.js/dist/video-js.css";
import "videojs-chapters-extension/dist/videojs-chapters-extension.css";
import "videojs-chapters-extension/src/css/fade.css";

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { setPlayTime } from '@modules/media';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// VideoJS 플러그인 등록
videojs.registerPlugin('chaptersMore', chaptersMore);

export const LocalizedPlayer = ({ setVideoDurationTime, lang, type }) => {
  const { uuid } = useParams();
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const dispatch = useDispatch();

  const selectedPlayTime = useSelector(state => state.media.selectedPlayTime);
  const cdn = useSelector(state => state.organization.cdn);
  const VSCdn = useSelector(state => state.organization.VSCdn);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // 플레이어 초기화
  useEffect(() => {
    if (!playerRef.current && uuid && VSCdn && cdn && lang) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const newPlayer = videojs(videoElement, {
        autoplay: false,
        playbackRates: [0.5, 1, 1.25, 1.5, 2],
        width: 720,
        height: 300,
        controls: true,
        autoCenter: false,
        sources: [
          {
            src: `${VSCdn}/hls/${uuid}/mp4/${uuid}.mp4/index.m3u8`,
            type: 'application/x-mpegURL',
          },
        ],
        plugins: {
          chaptersMore: {
            src: `${cdn}/${uuid}/chapter/${lang}/${uuid}-${lang}.json`
          }
        }
      }, () => {
        console.log("player is ready");
      });

      playerRef.current = newPlayer;
      setPlayer(newPlayer);
    }
  }, [uuid, VSCdn, cdn, lang]);

  // 플레이어 이벤트 핸들러 설정
  useEffect(() => {
    if (player) {
      player.on("loadedmetadata", () => {
        setVideoDurationTime(player.duration());
        player.addRemoteTextTrack({
          src: `${cdn}/${uuid}/subtitle/${lang}/${uuid}-${lang}.vtt`,
          label: lang,
          mode: "showing"
        }, true);
      });

      player.on("timeupdate", () => {
        const currentTime = player.currentTime();
        dispatch(setPlayTime(currentTime));
      });
    }
  }, [player, type, dispatch]);

  // 컴포넌트 언마운트 시 플레이어 정리
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
      }
    };
  }, []);

  // 선택된 재생 시간 변경 시 처리 (에디터 모드)
  useEffect(() => {
    if (type === "subtitle" && selectedPlayTime && playerRef.current) {
      playerRef.current.currentTime(selectedPlayTime);
    }
  }, [selectedPlayTime, type]);

  // 재생/정지 토글
  const checkPlaying = () => {
    if (!playerRef.current) return;

    if (playerRef.current.paused()) {
      playerRef.current.play();
    } else {
      playerRef.current.pause();
    }
  };

  // 자막과 챕터 새로고침 (기본 모드)
  const refreshTracks = () => {
    if (!playerRef.current) return;
    setIsRefreshing(true);
    const timestamp = Date.now();
    const player = playerRef.current;

    // 자막 제거
    const tracks = player.remoteTextTracks();
    for (let i = tracks.length - 1; i >= 0; i--) {
      const track = tracks[i];
      if (track.kind === "subtitles") {
        player.removeRemoteTextTrack(track);
      }
    }

    // 자막 다시 추가
    player.addRemoteTextTrack({
      src: `${cdn}/${uuid}/subtitle/${lang}/${uuid}-${lang}.vtt?v=${timestamp}`,
      label: lang,
      kind: "subtitles",
      mode: "showing"
    }, true);

    // 챕터 JSON fetch 후 플러그인 재초기화
    fetch(`${cdn}/${uuid}/chapter/${lang}/${uuid}-${lang}.json?v=${timestamp}`)
      .then(res => res.json())
      .then(() => {
        // 기존 플러그인 제거 후 재등록
        if (player.chaptersMore && typeof player.chaptersMore.dispose === "function") {
          player.chaptersMore.dispose();
        }
        delete player.chaptersMore;
        player.chaptersMore({
          src: `${cdn}/${uuid}/chapter/${lang}/${uuid}-${lang}.json?v=${timestamp}`
        });

        setIsRefreshing(false);
        setNotification({
          open: true,
          message: "자막과 챕터가 새로고침되었습니다!",
          severity: "success"
        });
      })
      .catch(err => {
        console.error("새로고침 실패", err);
        setIsRefreshing(false);
        setNotification({
          open: true,
          message: "자막과 챕터 새로고침에 실패했습니다.",
          severity: "error"
        });
      });
  };

  // 플레이어 완전 새로고침 (DefaultPlayer 모드)
  const refreshPlayer = () => {
    setIsRefreshing(true);
    const timestamp = Date.now();

    try {
      // 기존 플레이어 제거
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }

      // 비디오 컨테이너 참조
      const videoContainer = document.querySelector('.box-video');
      if (!videoContainer) {
        throw new Error("비디오 컨테이너를 찾을 수 없습니다");
      }

      // 새 비디오 요소 생성 및 추가
      const newVideoElement = document.createElement('video');
      newVideoElement.id = 'videoEl';
      newVideoElement.className = 'video-js vjs-big-play-centered';
      newVideoElement.style.height = '100%';
      newVideoElement.style.borderRadius = '4px';

      // 기존 내용 비우고 새 비디오 요소 추가
      videoContainer.innerHTML = '';
      videoContainer.appendChild(newVideoElement);

      // 비디오 참조 업데이트
      videoRef.current = newVideoElement;

      // 새 플레이어 인스턴스 생성
      const newPlayer = videojs(newVideoElement, {
        autoplay: false,
        playbackRates: [0.5, 1, 1.25, 1.5, 2],
        width: 720,
        height: 300,
        controls: true,
        sources: [
          {
            src: `${VSCdn}/hls/${uuid}/mp4/${uuid}.mp4/index.m3u8`,
            type: 'application/x-mpegURL',
          },
        ],
        plugins: {
          chaptersMore: {
            src: `${cdn}/${uuid}/chapter/${lang}/${uuid}-${lang}.json?v=${timestamp}`
          }
        }
      }, () => {
        console.log("player is ready after refresh");

        // 자막 다시 로드
        newPlayer.one("loadedmetadata", () => {
          setVideoDurationTime(newPlayer.duration());
          newPlayer.addRemoteTextTrack({
            src: `${cdn}/${uuid}/subtitle/${lang}/${uuid}-${lang}.vtt?v=${timestamp}`,
            label: lang,
            mode: "showing"
          }, true);
        });

        setIsRefreshing(false);
        setNotification({
          open: true,
          message: "플레이어가 성공적으로 새로고침되었습니다.",
          severity: "success"
        });
      });

      playerRef.current = newPlayer;
      setPlayer(newPlayer);

      // 기존 챕터 플러그인 제거
      if (newPlayer.chaptersMore && typeof newPlayer.chaptersMore.dispose === "function") {
        newPlayer.chaptersMore.dispose();
      }
      delete newPlayer.chaptersMore;

      // 에디터 타입이면 추가 이벤트 리스너 설정
      if (type === "subtitle") {
        newPlayer.on("timeupdate", () => {
          const currentTime = newPlayer.currentTime();
          // setCurrentPlayerTime(currentTime); // This line was removed from the original file
          dispatch(setPlayTime(currentTime));
        });
      }

    } catch (err) {
      console.error("새로고침 실패", err);
      setIsRefreshing(false);
      setNotification({
        open: true,
        message: "플레이어 새로고침에 실패했습니다.",
        severity: "error"
      });
    }
  };

  // 에디터 타입 비디오 컨테이너 렌더링
  const renderSubtitleVideoContainer = () => (
    <div className="video-container" style={{ position: "relative" }}>
      <button
        onClick={refreshPlayer}
        disabled={isRefreshing}
        className={`video-control-box ${isRefreshing ? 'video-control-box--disabled' : ''}`}
      >
        {isRefreshing ? <CircularProgress size={18} thickness={5} /> : <RestartAltIcon fontSize="small" />}
        <span>{isRefreshing ? "새로고침 중..." : "자막/챕터 새로고침"}</span>
      </button>

      <div className="box-video" style={{ width: '100%', height: '100%', borderRadius: 4 }}>
        <div data-vjs-player style={{ width: '100%', height: '100%', borderRadius: 4 }}>
          {
            videoRef ?
              <video ref={videoRef} className="video-js vjs-big-play-centered" style={{ width: '100%', height: '100%' }} />
              :
              <div style={{ width: "100%", height: "75%", paddingTop: '25%' }}>
                <Skeleton variant="rectangular" width="100%" height="100%" />
              </div>
          }
        </div>
      </div>
    </div>
  );

  // 기본 타입 비디오 컨테이너 렌더링
  const renderDefaultVideoContainer = () => (
    <>
      <button
        onClick={refreshPlayer}
        disabled={isRefreshing}
        className={`video-control-box ${isRefreshing ? 'video-control-box--disabled' : ''}`}
      >
        {isRefreshing ? <CircularProgress size={18} thickness={5} /> : <RestartAltIcon fontSize="small" />}
        <span>{isRefreshing ? "새로고침 중..." : "자막/챕터 새로고침"}</span>
      </button>

      <div className="box-video" style={{ width: '100%', height: '100%' }}>
        <div data-vjs-player style={{ width: '100%', height: '100%' }}>
          {
            videoRef ?
              <video
                ref={videoRef}
                id="videoEl"
                className="video-js vjs-big-play-centered"
                style={{ width: '100%', height: '100%' }}
              /> :
              <div className="localized-player__skeleton-container">
                <Skeleton variant="rectangular" width="100%" height="100%" />
                <PlayArrowIcon className="localized-player__play-icon" />
              </div>
          }
        </div>
      </div>
    </>
  );


  return (
    <div className="video-container"
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 4,
        position: 'relative'
      }}>
      {type === "subtitle" ? renderSubtitleVideoContainer() : renderDefaultVideoContainer()}

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          className="localized-player__alert"
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LocalizedPlayer;